import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { SearchType } from "Dashboard/domain/SearchType";
import { IGetStockInputsRequestParams } from "Dashboard/scenes/Stock/domain/IGetStockInputsRequestParams";
import { setSearchInputPopover } from "../inputSlice";
import { useAppDispatch } from "hooks/hooks";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetFocus,
  UseFormSetValue
} from "react-hook-form";
import DatePicker from "react-datepicker";
import { FormatDateIso } from "utils/dateUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import classes from "../../../../../../components/Popover/popover.module.scss";
import classesStock from "../../../popover.module.scss";
import PartnerSelectForSearch from "Dashboard/scenes/Partners/components/PartnerSelectForSearch";
import { styles } from "components/Popover/popoverSelectStyle";
import RepositorySelect from "./RepositorySelect";
import UnitSelect from "Dashboard/components/UnitSelect";
import TriStateCheckbox from "./TriStateCheckbox";

interface IProps {
  handleSubmit: UseFormHandleSubmit<IGetStockInputsRequestParams>;
  register: UseFormRegister<IGetStockInputsRequestParams>;
  control: Control<IGetStockInputsRequestParams, any>;
  getValues: UseFormGetValues<IGetStockInputsRequestParams>;
  reset: () => void;
  setFocus: UseFormSetFocus<IGetStockInputsRequestParams>;
  searchTypeValue: SearchType;
  setSearchTypeValue: React.Dispatch<React.SetStateAction<SearchType>>;
  setValue: UseFormSetValue<IGetStockInputsRequestParams>;
  setGetInputsRequestParams: Dispatch<SetStateAction<IGetStockInputsRequestParams>>;
};

const SearchPopover = (props: IProps) => {
  const dispatch = useAppDispatch();
  const { register, handleSubmit, control, getValues, setFocus, setValue }
    = props;

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(
    [getValues("dateFrom") ? new Date(getValues("dateFrom")!) : null,
    getValues("dateTo") ? new Date(getValues("dateTo")!) : null]);

  const close = () => {
    dispatch(setSearchInputPopover({ isOpen: false }));
  };

  const switchSearchMode = (searchType: SearchType) => {
    props.setSearchTypeValue(searchType);
  };

  const onSubmit = async (data: IGetStockInputsRequestParams) => {
    try {
      if (props.searchTypeValue === SearchType.BASIC) {
        data = { stock: data.stock, searchTerm: data.searchTerm };
      } else {
        data.searchTerm = undefined;
      }
      props.setGetInputsRequestParams(data);
      close();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (props.searchTypeValue === SearchType.BASIC) {
      setFocus("searchTerm");
    } else {
      setFocus("receiptNumber");
    }
  }, [setFocus, props.searchTypeValue]);

  return (
    <form
      className={`${classesStock.searchInputPopover} ${classes.basicPopover}`}
      onSubmit={handleSubmit(onSubmit)}
    >
      {props.searchTypeValue === SearchType.BASIC && (
        <>
          <input type="hidden" {...register("stock", { required: true })} />
          <div className={classes.row}>
            <div className={`${classes.col} ${classes.colDir}`}>
              <input
                type="text"
                placeholder=""
                className={`${classes.itemBorder} ${classes.fullWidth}`}
                {...register("searchTerm", { required: false })}
              />
            </div>
            <div className={classes.gPopoverDisclaimer}>
              * možete uneti više reči tj. kriterijuma pretrage
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.buttons}>
              <label
                onClick={() => switchSearchMode(SearchType.ADVANCED)}
                className={classes.clickableText}
              >
                NAPREDNA PRETRAGA
              </label>
              <button className={classes.iconButton} type="submit">
                <FontAwesomeIcon
                  icon={faLongArrowAltDown}
                  className={classes.icon} />
              </button>
            </div>
          </div>
        </>
      )}
      {props.searchTypeValue === SearchType.ADVANCED && (
        <>
          <input type="hidden" {...register("stock", { required: true })} />

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>br. računa</label>
              <input type="text" className={classes.itemBorder}
                {...register("receiptNumber", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>br. otpremnice</label>
              <input type="text" className={classes.itemBorder}
                {...register("billOfLandingId", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>šifra artikla</label>
              <input type="text" className={classes.itemBorder}
                {...register("code", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>ime</label>
              <input type="text" className={classes.itemBorder}
                {...register("name", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>magacin</label>
              <Controller
                name="repositoryId"
                control={control}
                defaultValue={undefined}
                rules={{
                  required: false,
                }}
                render={({ field: { onChange } }) => (
                  <RepositorySelect
                    value={getValues("repositoryId")}
                    onChange={onChange}
                    customStyles={styles.customStyleClassic}
                  />
                )}
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>dobavljač</label>
              <Controller
                name="supplier"
                control={control}
                defaultValue={undefined}
                rules={{
                  required: false,
                }}
                render={({ field: { onChange } }) => (
                  <PartnerSelectForSearch
                    value={getValues("supplier")}
                    onChange={onChange}
                    customStyles={styles.customStyleClassic}
                  />
                )}
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>količina</label>
              <input type="text" className={classes.itemBorder}
                {...register("quantityAmountTotalRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>mera</label>
              <Controller
                name="quantityUnit"
                control={control}
                defaultValue={undefined}
                rules={{
                  required: false,
                }}
                render={({ field: { onChange } }) => (
                  <UnitSelect
                    value={getValues("quantityUnit")}
                    onChange={onChange}
                    customStyles={styles.customStyleClassic}
                  />
                )}
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>cena po kom.</label>
              <input type="text" className={classes.itemBorder}
                {...register("pricePerUnitRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>cena transporta</label>
              <input type="text" className={classes.itemBorder}
                {...register("deliveryPriceRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>kol. ostalo</label>
              <input type="text" className={classes.itemBorder}
                {...register("quantityAmountRemainingRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>ukupno</label>
              <input type="text" className={classes.itemBorder}
                {...register("totalValueRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>datum ulaza</label>
              <div className="datepickerWrapper">
                <DatePicker
                  selectsRange={true}
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  showMonthDropdown
                  showYearDropdown
                  onChange={(date: any) => {
                    setDateRange([date[0], date[1]]);
                    setValue("dateFrom", date[0] && FormatDateIso(date[0]));
                    setValue("dateTo", date[1] && FormatDateIso(date[1]));
                  }}
                  isClearable={true}
                  className={`${classes.dateItem} ${classes.itemBorder}`}
                />
              </div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>napomena</label>
              <input type="text" className={classes.itemBorder}
                {...register("note", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>xxx</label>
              <Controller
                name="xxx"
                control={control}
                render={({ field }) => (
                  <div className={classesStock.triStateCh}>
                    <TriStateCheckbox
                      value={field.value}
                      onChange={field.onChange}
                    />
                    {(field.value !== true && field.value !== false)
                      && <div className={classes.gPopoverDisclaimer}>
                        Bilo koja vrednost kvačice
                      </div>}
                  </div>
                )}
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.buttons}>
              <label
                onClick={() => switchSearchMode(SearchType.BASIC)}
                className={classes.clickableText}
              >
                PAMETNA PRETRAGA
              </label>
              <button className={classes.iconButton} type="submit">
                <FontAwesomeIcon
                  icon={faLongArrowAltDown}
                  className={classes.icon} />
              </button>
            </div>
          </div>
        </>
      )}
    </form>
  );
};

export default SearchPopover;
